/**
 * Woher der Umsatz kommt — die Verteilung auf Kanäle und Direktbuchungen.
 *
 * <p>Die Hervorhebung im Belegungsverlauf hing bis Sprint 55 ausschließlich am Mauszeiger. Auf
 * Touch gab es diese Verbindung damit nicht. Jetzt ist jeder Legendeneintrag ein Schalter mit
 * `aria-pressed`; Hover hebt zusätzlich hervor, ersetzt den Schalter aber nicht (§9).</p>
 */
import { useMemo, useState } from 'react';
import { Card, CardBody, Eyebrow } from '@/shared/components';
import { EChart } from '@/shared/charts/EChart';
import { formatMoney } from '@/shared/lib/format';
import type { InsightsResponse } from '@/shared/api';
import { ChannelMixLegend } from './ChannelMixLegend';
import { buildChannelSlices } from './channelSlices';
import { channelColor, CHART_COLORS } from './financeChartTheme';

interface Props {
  insights: InsightsResponse;
  currency: string;
  /** Der gerade hervorgehobene Kanal — `null`, wenn keiner gewählt ist. */
  activeChannel: string | null;
  onChannelChange: (channel: string | null) => void;
}

export function ChannelMixDonut({ insights, currency, activeChannel, onChannelChange }: Props) {
  const slices = useMemo(() => buildChannelSlices(insights), [insights]);
  // Ein festgehaltener Kanal überlebt das Verlassen der Zeile. Ohne diese Trennung hätte ein
  // Klick mit der Maus keine Wirkung: `onMouseLeave` hätte die Auswahl sofort wieder gelöscht.
  const [pinned, setPinned] = useState<string | null>(null);

  const pin = (key: string | null) => {
    setPinned(key);
    onChannelChange(key);
  };
  const hover = (key: string | null) => {
    if (pinned === null) onChannelChange(key);
  };
  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        backgroundColor: CHART_COLORS.tooltipBg,
        borderColor: CHART_COLORS.grid,
        textStyle: { fontSize: 12, color: CHART_COLORS.axis },
        valueFormatter: (value: number) => formatMoney(value, currency),
      },
      series: [
        {
          type: 'pie',
          radius: ['55%', '85%'],
          padAngle: 2,
          label: { show: false },
          data: slices.map((slice) => ({
            name: slice.name,
            value: slice.revenue,
            key: slice.key,
            itemStyle: { color: channelColor(slice.key) },
          })),
        },
      ],
    }),
    [slices, currency],
  );

  if (slices.length === 0) {
    return (
      <Card>
        <CardBody className="p-4 sm:p-5">
          <Eyebrow variant="muted">Kanalmix</Eyebrow>
          <p className="text-ink-soft mt-4 text-sm">
            Im gewählten Zeitraum gibt es keine Buchung, deren Umsatz sich verteilen ließe.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="p-4 sm:p-5">
        <Eyebrow variant="muted">Kanalmix</Eyebrow>
        {/* Gestapelt bis `sm`: in fünf festen Spalten blieben dem Kanalnamen auf 360 px zwei
            Buchstaben — die Legende las sich als „A…", „M…", „B…". */}
        <div className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <EChart
            option={option}
            ariaLabel="Umsatz nach Kanal als Ring; die Werte stehen in der Legende daneben."
            className="h-40 min-h-0 sm:h-48"
            onHover={(params) =>
              hover(params ? ((params as { data?: { key?: string } }).data?.key ?? null) : null)
            }
          />
          <ChannelMixLegend
            slices={slices}
            activeKey={activeChannel}
            pinnedKey={pinned}
            onPin={pin}
            onHover={hover}
          />
        </div>
      </CardBody>
    </Card>
  );
}
